import React, { useMemo } from "react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { Label } from "src/base-components/Label";
import { Option, Select } from "src/base-components/Select";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useCheckFieldPermission } from "src/cases/templates/useCheckFieldPermission";
import { Icon } from "src/design-system/Icon";
import { Input } from "src/design-system/Input";
import { Tooltip } from "src/design-system/Tooltip";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { getEventIcon } from "src/eventsCatalogue/utils";
import { ErrorBoundary } from "src/utils/ErrorBoundary";

type EventTypesSelectorProps = {
  blockIndex: number;
};

type EventTypesFieldName =
  `attributes.case_blocks_configs.${number}.config.event_types`;

export const EventTypesSelector: React.FC<EventTypesSelectorProps> = (
  props,
) => (
  <ErrorBoundary
    fallbackErrorContent={
      <Input
        disabled
        error
        placeholder="Something went wrong, unable to load events"
      />
    }
  >
    <EventTypesSelectorInner {...props} />
  </ErrorBoundary>
);

const EventTypesSelectorInner: React.FC<EventTypesSelectorProps> = ({
  blockIndex,
}) => {
  const { control } = useFormContext<CaseTemplateFormValues>();
  const hasPermission = useCheckFieldPermission();
  const { data: eventSchemas, isLoading } = useEventSchemas();

  const fieldName =
    `attributes.case_blocks_configs.${blockIndex}.config.event_types` as EventTypesFieldName;
  const canEditConfig = hasPermission("edit", fieldName);

  const options: Option[] = useMemo(
    () =>
      eventSchemas?.map((schema) => ({
        key: schema.event_type,
        value: schema.display_name_plural || schema.event_type,
        icon: getEventIcon(schema),
      })) ?? [],
    [eventSchemas],
  );

  // Hide field if workspace has no event types
  if (!eventSchemas || eventSchemas.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="mb-2 flex items-center gap-1">
        <Label mb="mb-0" required>
          Event types the reviewer will see
        </Label>
        <Tooltip title="Select which event types will be visible to reviewers. By default, all event types are shown.">
          <Icon icon={faInfoCircle} size="xs" />
        </Tooltip>
      </div>
      <Controller
        control={control}
        name={fieldName}
        render={({ field }) => {
          const { onChange, value } = field;

          return (
            <Select
              disabled={isLoading || !canEditConfig}
              loading={isLoading}
              options={options}
              placeholder="Select event types"
              searchPlaceholder="Search event types..."
              searchable={options.length > 5}
              value={getDisplayedEventTypes(value, options)}
              fullWidth
              multiple
              onChange={onChange}
            />
          );
        }}
      />
    </div>
  );
};

/**
 * Makes sure that we display always only existing event types
 */
const getDisplayedEventTypes = (
  value: string[] | null | undefined,
  options: Option[],
) => {
  if (!value) {
    return options.map((opt) => opt.key);
  }

  return value.filter((v) => options.find((o) => o.key === v));
};
